import type { BorderProps, LayoutProps, ShadowProps, SpacingProps } from '@shopify/restyle';
import { border, createRestyleComponent, layout, shadow, spacing } from '@shopify/restyle';
import type { ImageProps, ImageURISource } from 'react-native';
import { ActivityIndicator } from 'react-native';
import type { ImageErrorEventData, ImageProps as ExpoImageProps } from 'expo-image';
import { Image as ExpoImage } from 'expo-image';

import { useEffect, useRef, useState } from 'react';
import * as FileSystem from 'expo-file-system';
import { Row } from 'app/design/primitives/box';
import { isMobileNative } from 'app/utils/web-helpers';
import { getMasterImageUrl, triggerCreateImageVariantsFromUrl } from 'app/lib/image-variants';
import { getImgProxyUrl } from 'app/lib/imgProxy';
import type { Theme } from 'app/shopify';
import { getLocalCacheForImage } from './image.utils';

export { getLocalCacheForImage } from './image.utils';

type RestyleProps = BorderProps<Theme> & ShadowProps<Theme> & SpacingProps<Theme> & LayoutProps<Theme>;

const IMAGE_VARIANT_DIMENSIONS = {
  mini: 200,
  small: 400,
  medium: 800,
  large: 1600,
} as const;

function getVariantDimensionsFromUrl(url?: string | null): { width: number; height: number } | undefined {
  if (typeof url !== 'string') return undefined;

  const match = /__(mini|small|medium|large)(?=\.[^./?#]+(?:[?#].*)?$|$|[?#])/.exec(url);
  if (!match) return undefined;

  const variant = match[1] as keyof typeof IMAGE_VARIANT_DIMENSIONS;
  const size = IMAGE_VARIANT_DIMENSIONS[variant];
  return { width: size, height: size };
}

async function loadImageFromOwnCache({
  currentUri,
  fileURI,
  headers,
  isComponentMounted,
  updateImgUri,
}: {
  currentUri: string;
  fileURI: string;
  headers: ImageURISource['headers'];
  isComponentMounted: () => boolean;
  updateImgUri: (uri: string | null) => void;
}) {
  // Defer state changes until after the effect setup has completed.
  await Promise.resolve();
  const localCacheFile = new FileSystem.File(fileURI);
  const metadata = localCacheFile.info();

  if (!metadata.exists || metadata.size === 0) {
    updateImgUri(null);
    if (isComponentMounted()) {
      const requestOption = headers ? { headers, idempotent: true } : { idempotent: true };
      const output = await FileSystem.File.downloadFileAsync(currentUri, localCacheFile, requestOption);
      if (isComponentMounted()) {
        updateImgUri(output.uri);
      }
    }
  } else {
    updateImgUri(fileURI);
  }
}

// Use Expo's source type directly: intersecting it with React Native's expands
// the SF Symbols union and can exhaust the TypeScript compiler's heap.
type PCImageProps = Omit<ImageProps, 'onError' | 'source'> & ExpoImageProps & RestyleProps & { useOwnCache?: boolean };

function getImageConfiguration(source: PCImageProps['source'], useOwnCache?: boolean) {
  const imageSource = typeof source === 'object' ? (source as ImageURISource | undefined) : undefined;
  const uri = imageSource?.uri;
  const safeUri = typeof uri === 'string' ? uri : undefined;
  const canUseOwnCache =
    isMobileNative &&
    typeof source === 'object' &&
    source !== null &&
    'uri' in source &&
    !source.uri?.includes('.svg') &&
    !source.uri?.includes('file:');
  const fileURI = safeUri ? getLocalCacheForImage(safeUri)! : '_';
  const masterImageUri = getMasterImageUrl(safeUri);
  const variantDimensions = getVariantDimensionsFromUrl(safeUri);
  const fallbackProxyUri =
    masterImageUri &&
    variantDimensions &&
    masterImageUri !== safeUri &&
    !masterImageUri.includes('file:') &&
    !masterImageUri.includes('.svg')
      ? getImgProxyUrl(masterImageUri, variantDimensions.width, variantDimensions.height)
      : undefined;
  const ownCacheRequested = Boolean(useOwnCache && canUseOwnCache && !safeUri?.includes('file:'));
  return {
    fallbackProxyUri,
    fileURI,
    headers: imageSource?.headers,
    imageSource,
    initialImgUri: !safeUri ? null : ownCacheRequested ? fileURI : safeUri,
    ownCacheRequested,
    safeUri,
  };
}

function getRenderSource(
  imageSource: ImageURISource | undefined,
  imgUri: string | null,
  source: PCImageProps['source'],
): ExpoImageProps['source'] {
  if (imageSource && imgUri) return { ...imageSource, uri: imgUri };
  const sourceAcrossImageLibraries: unknown = source;
  return sourceAcrossImageLibraries as ExpoImageProps['source'];
}

function PCImageContent({
  handleImageError,
  imageSource,
  imgUri,
  rest,
  safeUri,
  shouldUseOwnCache,
  source,
}: {
  handleImageError: (error: ImageErrorEventData) => void;
  imageSource?: ImageURISource;
  imgUri: string | null;
  rest: Omit<PCImageProps, 'source' | 'onError'>;
  safeUri?: string;
  shouldUseOwnCache: boolean;
  source: PCImageProps['source'];
}) {
  if (!shouldUseOwnCache) {
    const { cachePolicy, ...otherProps } = rest;
    const renderSource = getRenderSource(imageSource, imgUri, source);
    return (
      <RestyledImage
        cachePolicy={safeUri?.includes('.svg') ? 'none' : cachePolicy}
        onError={handleImageError}
        source={renderSource}
        {...otherProps}
      />
    );
  }

  if (!imgUri) {
    return (
      <Row alignItems="center">
        <ActivityIndicator />
      </Row>
    );
  }

  const renderSource = getRenderSource(imageSource, imgUri, source);
  return <RestyledImage onError={handleImageError} {...rest} source={renderSource} />;
}

export function PCImage(props: PCImageProps) {
  const { source, onError, ...rest } = props;
  const { fallbackProxyUri, fileURI, headers, imageSource, initialImgUri, ownCacheRequested, safeUri } =
    getImageConfiguration(source, props.useOwnCache);
  const [imageState, setImageState] = useState({
    sourceUri: safeUri,
    ownCacheRequested,
    imgUri: initialImgUri,
    ownCacheFailed: false,
  });
  const stateMatchesSource = imageState.sourceUri === safeUri && imageState.ownCacheRequested === ownCacheRequested;
  const currentImageState = stateMatchesSource
    ? imageState
    : { sourceUri: safeUri, ownCacheRequested, imgUri: initialImgUri, ownCacheFailed: false };
  if (!stateMatchesSource) {
    setImageState(currentImageState);
  }
  const imgUri = currentImageState.imgUri;
  const setImgUri = (nextImgUri: string | null) => {
    setImageState((current) => ({
      sourceUri: safeUri,
      ownCacheRequested,
      imgUri: nextImgUri,
      ownCacheFailed: current.sourceUri === safeUri ? current.ownCacheFailed : false,
    }));
  };

  const didFallbackToProxyUri = useRef(false);
  const shouldUseOwnCache = ownCacheRequested && !currentImageState.ownCacheFailed;

  const fallbackToProxyImage = () => {
    if (!fallbackProxyUri || fallbackProxyUri === safeUri || didFallbackToProxyUri.current) {
      return false;
    }

    didFallbackToProxyUri.current = true;
    setImgUri(fallbackProxyUri);
    triggerCreateImageVariantsFromUrl(safeUri);
    return true;
  };

  const handleImageError = (error: ImageErrorEventData) => {
    // A nonempty file can still be unreadable. Retry the original URL before
    // treating this as a missing remote variant or reporting a terminal error.
    if (shouldUseOwnCache && imgUri === fileURI && safeUri) {
      setImageState((current) =>
        current.sourceUri === safeUri ? { ...current, imgUri: safeUri, ownCacheFailed: true } : current,
      );
      return;
    }

    if (fallbackToProxyImage()) {
      return;
    }

    onError?.(error);
  };

  useEffect(() => {
    didFallbackToProxyUri.current = false;
  }, [safeUri]);

  useEffect(() => {
    if (!shouldUseOwnCache || !safeUri) {
      return;
    }

    const currentUri = safeUri;
    let isActive = true;
    const updateImgUri = (nextImgUri: string | null) => {
      if (!isActive) return;
      setImageState((current) =>
        current.sourceUri === currentUri && !current.ownCacheFailed ? { ...current, imgUri: nextImgUri } : current,
      );
    };
    const isComponentMounted = () => isActive;
    void loadImageFromOwnCache({ currentUri, fileURI, headers, isComponentMounted, updateImgUri }).catch(
      (err: unknown) => {
        if (!isActive) return;
        if (fallbackProxyUri && fallbackProxyUri !== currentUri && !didFallbackToProxyUri.current) {
          didFallbackToProxyUri.current = true;
          updateImgUri(fallbackProxyUri);
          triggerCreateImageVariantsFromUrl(currentUri);
          return;
        }

        if (!(err instanceof Error) || !err.message.includes('status 403')) {
          console.error('Error loading image:', err);
        }
        setImageState({
          sourceUri: currentUri,
          ownCacheRequested,
          imgUri: currentUri,
          ownCacheFailed: true,
        });
      },
    );
    return () => {
      isActive = false;
    };
  }, [fallbackProxyUri, fileURI, headers, ownCacheRequested, safeUri, shouldUseOwnCache]);

  return (
    <PCImageContent
      handleImageError={handleImageError}
      imageSource={imageSource}
      imgUri={imgUri}
      rest={rest}
      safeUri={safeUri}
      shouldUseOwnCache={shouldUseOwnCache}
      source={source}
    />
  );
}

const RestyledImage = createRestyleComponent<
  RestyleProps &
    Omit<
      ImageProps,
      | 'borderBottomLeftRadius'
      | 'borderBottomRightRadius'
      | 'borderRadius'
      | 'borderTopLeftRadius'
      | 'borderTopRightRadius'
      | 'onError'
      | 'source'
    > &
    Omit<
      ExpoImageProps,
      | 'borderBottomLeftRadius'
      | 'borderBottomRightRadius'
      | 'borderRadius'
      | 'borderTopLeftRadius'
      | 'borderTopRightRadius'
    >,
  Theme
>([border, layout, shadow, spacing], ExpoImage);
